// /admin — founder-only quick-invite page.
//
// One input: paste an email, optionally tag a source, click Add. Calls
// /api/admin/whitelist which checks the caller's JWT email against
// ADMIN_EMAILS env var; non-admin callers get a clean "Forbidden" message
// and bounce to /.
//
// Keeps a running list of who was added this session so you don't lose
// track during a 5-DM-rush — useful for following up later.

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Plus, ShieldCheck, X } from "lucide-react";
import BackButton from "../components/BackButton";
import { apiFetch } from "../lib/apiFetch";
import { friendlyError } from "../lib/errors";
import { supabase } from "../lib/supabase";

interface RecentInvite {
  email: string;
  source: string;
  alreadyWhitelisted: boolean;
  ts: number;
}

const SOURCE_PRESETS = [
  "trainer-outreach",
  "personal-network",
  "twitter-reply",
  "reddit-dm",
  "friend-of-james",
  "manual-admin"
];

export default function Admin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [source, setSource] = useState("trainer-outreach");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentInvite[]>([]);
  /** Set to true after first successful or 403 response so we know the
   *  caller is at least authenticated. Used to swap the form for a
   *  forbidden message gracefully. */
  const [forbidden, setForbidden] = useState(false);

  // Soft auth gate — bounce out if not signed in at all. The real admin
  // check happens server-side via ADMIN_EMAILS.
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) navigate("/");
    });
  }, [navigate]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await apiFetch("/api/admin/whitelist", {
        method: "POST",
        body: JSON.stringify({ email: trimmed, source })
      });
      if (resp.status === 403) {
        setForbidden(true);
        return;
      }
      if (!resp.ok) {
        const data = (await resp.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? `Server returned ${resp.status}`);
      }
      const data = (await resp.json()) as { email: string; alreadyWhitelisted: boolean };
      setRecent((prev) => [
        {
          email: data.email,
          source,
          alreadyWhitelisted: data.alreadyWhitelisted,
          ts: Date.now()
        },
        ...prev
      ].slice(0, 20));
      setEmail("");
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  if (forbidden) {
    return (
      <div className="p-6 space-y-4 max-w-md mx-auto">
        <BackButton onClick={() => navigate("/")} label="Home" />
        <div className="p-5 rounded-2xl bg-slate-900 border border-rose-900/60 text-center space-y-3">
          <ShieldCheck className="w-10 h-10 text-rose-400 mx-auto" strokeWidth={1.8} />
          <h2 className="text-base font-semibold text-slate-100">Admins only</h2>
          <p className="text-sm text-slate-400 leading-relaxed">
            This page only works for accounts whose email is in the ADMIN_EMAILS env list.
            If you should have access, ask James to add your email.
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-2 px-4 py-2 rounded-lg bg-slate-800 text-slate-200 text-sm hover:bg-slate-700"
          >
            Back to app
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-md mx-auto pb-24">
      <BackButton onClick={() => navigate("/")} label="Home" />

      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-slate-100">Invite to beta</h1>
        <p className="text-sm text-slate-400 leading-relaxed">
          Adds the email to <span className="text-emerald-300 font-medium">beta_whitelist</span> so they can sign up at <span className="text-emerald-300">app.tallyplate.app</span>. They'll get a magic link, no code needed.
        </p>
      </header>

      <form onSubmit={submit} className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="trainer@example.com"
            required
            autoFocus
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Source</span>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-emerald-500"
          >
            {SOURCE_PRESETS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <span className="text-[11px] text-slate-500 mt-1 block">Stored in beta_whitelist.source for later cohort analysis.</span>
        </label>

        {error && (
          <div className="p-2.5 rounded-lg bg-red-950 border border-red-800 text-red-200 text-xs flex items-start gap-2">
            <X className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="w-full py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {busy ? "Adding…" : "Add to whitelist"}
        </button>
      </form>

      {recent.length > 0 && (
        <section className="space-y-2">
          <div className="text-xs font-medium text-slate-400 uppercase tracking-wider px-1">
            Added this session
          </div>
          <ul className="rounded-2xl bg-slate-900 border border-slate-800 divide-y divide-slate-800 overflow-hidden">
            {recent.map((r) => (
              <li key={`${r.email}-${r.ts}`} className="px-3 py-2.5 flex items-center gap-2.5">
                <Check className={`w-4 h-4 flex-shrink-0 ${r.alreadyWhitelisted ? "text-amber-400" : "text-emerald-400"}`} strokeWidth={2.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 truncate">{r.email}</div>
                  <div className="text-[11px] text-slate-500">
                    {r.alreadyWhitelisted ? "Already on the list" : `Added · ${r.source}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <details className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-slate-400">
        <summary className="cursor-pointer text-slate-300 font-medium">How they sign up</summary>
        <ol className="mt-2 space-y-1.5 list-decimal list-inside leading-relaxed">
          <li>They visit <span className="text-emerald-300">app.tallyplate.app</span></li>
          <li>Click "Sign up", enter the same email you added</li>
          <li>Magic link arrives — tap it, they're in</li>
          <li>No invite code needed (the whitelist row IS their pass)</li>
        </ol>
      </details>
    </div>
  );
}
